import {
  PayrollQueue,
  PayrollQueueQuery,
  PayrollRow,
  SettingView,
  ShareworksClaimResult,
  ShareworksEnteredResult,
  ShareworksWorklist,
  type ShareworksGrant,
  StatementDecisionResult,
  StatementList,
  type PayLine,
  type StatementDecision,
} from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { request } from './client';

// After the letters (APP-043): Payroll's queue and payment (P1), the Stock Admin's earning
// statement check (SA6) and the Shareworks worklist (SA5), plus the one setting Payroll keeps
// (the calculator's tax estimate). Every award write bumps the award's `version`, which all of
// its people's rows share: after one, the whole list is read again, never patched row by row.

/** Ids come from the server's rows: only a UUID ever reaches a request path. */
const Id = z.uuid();

function path(id: string): string {
  return encodeURIComponent(Id.parse(id));
}

// --- Payroll (P1) -----------------------------------------------------------------------------------

export const payrollKeys = { queue: ['payroll', 'queue'] as const };

/** Filtered pages; every variant shares a prefix so pay/upload invalidates all views. */
export function payrollListQuery(input: Partial<PayrollQueueQuery> = {}) {
  const query = PayrollQueueQuery.parse(input);
  // The bare address is the Hero's counts query; any paged, sized or grouped request (APP-248's
  // Spot section and the other awards) keys on its own.
  const isDefault =
    query.view === 'in_progress' &&
    query.group === 'all' &&
    query.page === 1 &&
    query.size === undefined;
  const params = new URLSearchParams();
  if (!isDefault) {
    params.set('view', query.view);
    if (query.group !== 'all') params.set('group', query.group);
    if (query.view === 'completed') params.set('period', query.period);
    if (query.from !== undefined) params.set('from', query.from);
    if (query.to !== undefined) params.set('to', query.to);
    params.set('page', String(query.page));
    if (query.size !== undefined) params.set('size', String(query.size));
  }
  return queryOptions({
    queryKey: isDefault ? payrollKeys.queue : [...payrollKeys.queue, query],
    queryFn: ({ signal }) =>
      request(`/api/v1/payroll/queue${params.size === 0 ? '' : `?${params.toString()}`}`, {
        schema: PayrollQueue,
        signal,
      }),
    staleTime: 15_000,
    refetchInterval: (q) =>
      q.state.data?.rows.some((r) => r.statement?.scanStatus === 'pending') === true
        ? 5_000
        : false,
  });
}
export const payrollQuery = payrollListQuery();

/** Mark one person paid with the final tax; the cash and whole shares follow from it. */
export function payLine(lineId: string, body: PayLine): Promise<PayrollRow> {
  return request(`/api/v1/payroll/lines/${path(lineId)}/pay`, {
    method: 'POST',
    body,
    schema: PayrollRow,
  });
}

// --- Earning statements (SA6) -----------------------------------------------------------------------

export const statementKeys = { list: ['statements'] as const };

/** Every paid cash line at the statement check, and the last month's decided ones. */
export const statementsQuery = queryOptions({
  queryKey: statementKeys.list,
  queryFn: ({ signal }) => request('/api/v1/statements', { schema: StatementList, signal }),
  staleTime: 15_000,
});

/** Confirm a matching statement, or record Payroll's numbers with a reason. */
export function decideStatement(
  lineId: string,
  body: StatementDecision,
): Promise<StatementDecisionResult> {
  return request(`/api/v1/statements/${path(lineId)}/decision`, {
    method: 'POST',
    body,
    schema: StatementDecisionResult,
  });
}

// --- Shareworks (SA5) -------------------------------------------------------------------------------

export const shareworksKeys = { list: ['shareworks'] as const };

/** Every grant waiting for Shareworks, who's on which, and today's check-offs. */
export const shareworksQuery = queryOptions({
  queryKey: shareworksKeys.list,
  queryFn: ({ signal }) => request('/api/v1/shareworks', { schema: ShareworksWorklist, signal }),
  staleTime: 15_000,
  // Others claim and check off rows while you work: keep "who's on what" current.
  refetchInterval: 60_000,
});

/** Claim rows (one or a batch): the ones someone else holds come back as taken. */
export function claimShareworks(lineIds: readonly string[]): Promise<ShareworksClaimResult> {
  return request('/api/v1/shareworks/claim', {
    method: 'POST',
    body: { lineIds: lineIds.map((id) => Id.parse(id)) },
    schema: ShareworksClaimResult,
  });
}

/** Hand your own rows back. */
export function releaseShareworks(lineIds: readonly string[]): Promise<ShareworksWorklist> {
  return request('/api/v1/shareworks/release', {
    method: 'POST',
    body: { lineIds: lineIds.map((id) => Id.parse(id)) },
    schema: ShareworksWorklist,
  });
}

/** One grant on the worklist: a person's options, or the shares their election bought. */
export interface GrantRef {
  lineId: string;
  grant: ShareworksGrant;
}

const grantsBody = (grants: readonly GrantRef[]) => ({
  grants: grants.map((g) => ({ lineId: Id.parse(g.lineId), grant: g.grant })),
});

/**
 * Check grants off as entered in Shareworks (APP-067 §6). All or nothing: every grant must be on a
 * row you hold. A person closes once all their grants are entered.
 */
export function enteredShareworks(grants: readonly GrantRef[]): Promise<ShareworksEnteredResult> {
  return request('/api/v1/shareworks/entered', {
    method: 'POST',
    body: grantsBody(grants),
    schema: ShareworksEnteredResult,
  });
}

/** Undo a check-off made by mistake, while the person's other grant still waits. */
export function undoShareworks(grants: readonly GrantRef[]): Promise<ShareworksWorklist> {
  return request('/api/v1/shareworks/undo', {
    method: 'POST',
    body: grantsBody(grants),
    schema: ShareworksWorklist,
  });
}

/** The worklist as CSV: the server reads it again with your access, and logs the export. */
export const SHAREWORKS_EXPORT_HREF = '/api/v1/shareworks/export';

// --- Settings ---------------------------------------------------------------------------------------

/** Only registry keys ("payroll.earningStatementRequired") reach a request path. */
const Key = z.string().regex(/^[a-z][A-Za-z]*\.[A-Za-z]+$/);

export const settingKeys = {
  one: (key: string, scope: string) => ['settings', key, scope] as const,
};

export function settingQuery(key: string, scope = 'system') {
  return queryOptions({
    queryKey: settingKeys.one(key, scope),
    queryFn: ({ signal }) =>
      request(`/api/v1/settings/${Key.parse(key)}?scope=${encodeURIComponent(scope)}`, {
        schema: SettingView,
        signal,
      }),
    staleTime: 60_000,
  });
}
